import { create } from 'zustand'
import { applyAction, type GameAction } from '../game/actions'
import { botStep } from '../game/botTurn'
import {
  canEndTurn,
  commitMove,
  createGame,
  cur,
  diceTotal,
  hasTurnOptions,
  reroll,
  startGame,
  type PlayerConfig,
} from '../game/engine'
import type { GameSettings, GameState } from '../game/types'
import { NetClient, clearRoomFromUrl, type NetStatus } from '../net/client'
import { ERROR_TEXT, ROLL_ANIM_MS, type RoomView, type ServerMsg } from '../net/protocol'

const SAVE_KEY = 'millo.save.v1'
const TOKEN_KEY = 'millo.token.v1'
const AUTOEND_KEY = 'millo.autoend.v1'

/** จบตาอัตโนมัติเร็ว เมื่อไม่มีอะไรให้ตัดสินใจแล้ว */
const AUTO_END_FAST = 1400
/** ช้าลงเมื่อยังอัปเกรด/ไถ่ถอนได้ จะได้มีเวลาคิด */
const AUTO_END_SLOW = 5000

let moveTimer: ReturnType<typeof setTimeout> | null = null
let botTimer: ReturnType<typeof setTimeout> | null = null
let endTimer: ReturnType<typeof setTimeout> | null = null
let net: NetClient | null = null

function clearTimers() {
  if (moveTimer) clearTimeout(moveTimer)
  if (botTimer) clearTimeout(botTimer)
  if (endTimer) clearTimeout(endTimer)
  moveTimer = null
  botTimer = null
  endTimer = null
}

function readAutoEnd(): boolean {
  try {
    return localStorage.getItem(AUTOEND_KEY) !== 'off'
  } catch {
    return true
  }
}

export type Mode = 'local' | 'online'

export interface NetState {
  status: NetStatus
  code: string | null
  seatId: number | null
  room: RoomView | null
  error: string | null
}

const IDLE_NET: NetState = { status: 'idle', code: null, seatId: null, room: null, error: null }

interface Store {
  g: GameState | null
  mode: Mode
  net: NetState
  inspecting: number | null
  showRules: boolean

  /* ---- จบตาอัตโนมัติ ---- */
  autoEnd: boolean
  /** ผู้เล่นขอเบรกไว้ก่อน (กดจัดการพอร์ต หรือกดปุ่มหยุด) — รีเซ็ตทุกครั้งที่เปลี่ยนตา */
  turnHeld: boolean
  /** เวลาที่จะจบตาเอง ใช้วาดแถบนับถอยหลัง */
  autoEndAt: number | null
  setAutoEnd: (v: boolean) => void
  holdTurn: () => void

  /* ---- เล่นเครื่องเดียว ---- */
  newGame: (players: PlayerConfig[], settings?: Partial<GameSettings>, seed?: number) => void
  resume: () => boolean
  quit: () => void
  apply: (fn: (g: GameState) => void) => void

  /* ---- ห้องออนไลน์ ---- */
  createRoom: (name: string, characterId: string, professionId: string) => void
  joinRoom: (code: string, name: string, characterId: string, professionId: string) => void
  tryRejoin: () => boolean
  updateSeat: (patch: { name?: string; characterId?: string; professionId?: string }) => void
  addBot: () => void
  removeSeat: (id: number) => void
  updateSettings: (patch: Partial<GameSettings>) => void
  startOnline: () => void
  leaveRoom: () => void
  dismissNetError: () => void

  /* ---- คำสั่งในเกม (ชื่อเดิม ใช้ได้ทั้งสองโหมด) ---- */
  roll: () => void
  useReroll: () => void
  acceptRoll: () => void
  confirm: () => void
  buy: (mode: 'cash' | 'loan') => void
  pass: () => void
  takeDeal: (cardId?: string) => void
  passDeal: () => void
  upgrade: (tileId: number) => void
  mortgage: (tileId: number) => void
  unmortgage: (tileId: number) => void
  dumpProperty: (tileId: number) => void
  dumpAsset: (uid: string) => void
  bail: () => void
  finishTurn: () => void
  surrender: () => void

  inspect: (id: number | null) => void
  toggleRules: (v?: boolean) => void
  botStep: () => void
}

/** ผู้ใช้เครื่องนี้สั่งการแทนผู้เล่นคนปัจจุบันได้ไหม */
export function controlsCurrent(s: { g: GameState | null; mode: Mode; net: NetState }): boolean {
  const g = s.g
  if (!g || g.phase === 'gameover') return false
  if (s.mode === 'online') return g.current === s.net.seatId
  return !g.players[g.current].isBot
}

/** true = กำลังรอให้ผู้เล่นกดยืนยันแต้มเต๋า เพราะยังมีสิทธิ์ทอยใหม่ */
export function awaitingRollChoice(s: { g: GameState | null; mode: Mode; net: NetState }): boolean {
  const g = s.g
  if (!g || !g.rolling) return false
  return controlsCurrent(s) && cur(g).rerollLeft > 0
}

function saveToken(code: string, token: string) {
  try {
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify({ code, token }))
  } catch {
    /* โหมดส่วนตัวอาจบล็อก */
  }
}

function readToken(): { code: string; token: string } | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY)
    return raw ? (JSON.parse(raw) as { code: string; token: string }) : null
  } catch {
    return null
  }
}

export const useGame = create<Store>((set, get) => {
  /* ---------------- ตัวจับเวลาโหมดเล่นเครื่องเดียว ---------------- */

  function scheduleMove() {
    if (moveTimer || get().mode === 'online') return
    const g = get().g
    if (!g?.rolling) return
    if (awaitingRollChoice(get())) return
    moveTimer = setTimeout(() => {
      moveTimer = null
      get().apply((draft) => {
        const p = cur(draft)
        if (p.isBot && p.rerollLeft > 0 && diceTotal(draft) <= 4) reroll(draft)
        else commitMove(draft)
      })
    }, ROLL_ANIM_MS)
  }

  function scheduleBot() {
    if (botTimer || get().mode === 'online') return
    const g = get().g
    if (!g || g.phase === 'gameover' || g.rolling) return
    if (!cur(g).isBot) return
    botTimer = setTimeout(() => {
      botTimer = null
      get().botStep()
    }, g.settings.botSpeed)
  }

  /**
   * ตั้งเวลาจบตาให้เอง — ปุ่ม "จบตา" แทบไม่มีทางเลือกอื่นอยู่แล้ว
   * แต่ยังเว้นจังหวะให้อัปเกรด/ไถ่ถอนได้ และหยุดทันทีถ้าผู้เล่นเริ่มจัดการพอร์ต
   */
  function scheduleAutoEnd() {
    if (endTimer) clearTimeout(endTimer)
    endTimer = null
    const s = get()
    if (s.autoEndAt !== null) set({ autoEndAt: null })
    const g = s.g
    if (!g || !s.autoEnd || s.turnHeld) return
    if (!controlsCurrent(s) || !canEndTurn(g)) return

    const delay = hasTurnOptions(g) ? AUTO_END_SLOW : AUTO_END_FAST
    set({ autoEndAt: Date.now() + delay })
    endTimer = setTimeout(() => {
      endTimer = null
      set({ autoEndAt: null })
      const now = get()
      if (!now.g || now.turnHeld || !now.autoEnd) return
      if (!controlsCurrent(now) || !canEndTurn(now.g)) return
      now.finishTurn()
    }, delay)
  }

  function afterUpdate() {
    if (get().mode !== 'online') {
      scheduleMove()
      scheduleBot()
      const g = get().g
      if (g) {
        try {
          localStorage.setItem(SAVE_KEY, JSON.stringify(g))
        } catch {
          /* โหมดส่วนตัวของเบราว์เซอร์อาจบล็อก — ไม่เป็นไร */
        }
      }
    }
    scheduleAutoEnd()
  }

  /** ตั้งสถานะใหม่ พร้อมล้างการเบรกเมื่อเปลี่ยนตาผู้เล่น */
  function commit(next: GameState) {
    const before = get().g
    const turnChanged = !before || before.current !== next.current || before.round !== next.round
    set(turnChanged ? { g: next, turnHeld: false } : { g: next })
    afterUpdate()
  }

  const apply: Store['apply'] = (fn) => {
    const current = get().g
    if (!current) return
    const next: GameState = structuredClone(current)
    fn(next)
    commit(next)
  }

  /* ---------------- เครือข่าย ---------------- */

  function handle(msg: ServerMsg) {
    switch (msg.t) {
      case 'welcome': {
        saveToken(msg.code, msg.token)
        net?.setHello({ t: 'rejoin', code: msg.code, token: msg.token })
        clearRoomFromUrl()
        set((s) => ({
          mode: 'online',
          g: msg.g,
          turnHeld: false,
          net: { ...s.net, code: msg.code, seatId: msg.seatId, room: msg.room, error: null },
        }))
        afterUpdate()
        break
      }
      case 'room':
        set((s) => ({ net: { ...s.net, room: msg.room }, g: msg.room.started ? s.g : null }))
        break
      case 'state': {
        set((s) => ({ net: { ...s.net, room: msg.room } }))
        commit(msg.g)
        break
      }
      case 'error': {
        const text = msg.message && msg.message !== msg.code ? msg.message : ERROR_TEXT[msg.code]
        // "ยังไม่ถึงตาของคุณ" เกิดจากกดรัวตอนสถานะยังไม่ทันอัปเดต ไม่ต้องรบกวนผู้เล่น
        if (msg.code === 'not-your-turn') break
        set((s) => ({ net: { ...s.net, error: text } }))
        if (msg.code === 'no-room' || msg.code === 'bad-token') {
          try {
            sessionStorage.removeItem(TOKEN_KEY)
          } catch {
            /* ไม่เป็นไร */
          }
        }
        break
      }
      case 'bye':
        set((s) => ({ net: { ...s.net, error: msg.message } }))
        break
      default:
        break
    }
  }

  function ensureNet(): NetClient {
    if (net) return net
    net = new NetClient({
      onMessage: handle,
      onStatus: (status) => set((s) => ({ net: { ...s.net, status } })),
    })
    return net
  }

  const send = (action: GameAction) => {
    const s = get()
    if (s.mode === 'online') {
      net?.send({ t: 'action', action })
      return
    }
    s.apply((g) => applyAction(g, action))
  }

  return {
    g: null,
    mode: 'local',
    net: IDLE_NET,
    inspecting: null,
    showRules: false,

    autoEnd: readAutoEnd(),
    turnHeld: false,
    autoEndAt: null,

    setAutoEnd: (v) => {
      try {
        localStorage.setItem(AUTOEND_KEY, v ? 'on' : 'off')
      } catch {
        /* ไม่เป็นไร */
      }
      set({ autoEnd: v })
      scheduleAutoEnd()
    },

    holdTurn: () => {
      if (endTimer) clearTimeout(endTimer)
      endTimer = null
      set({ turnHeld: true, autoEndAt: null })
    },

    /* ---------------- เล่นเครื่องเดียว ---------------- */

    newGame: (players, settings, seed) => {
      clearTimers()
      const g = createGame({ players, settings, seed })
      startGame(g)
      set({ g, mode: 'local', net: IDLE_NET, inspecting: null })
      afterUpdate()
    },

    resume: () => {
      try {
        const raw = localStorage.getItem(SAVE_KEY)
        if (!raw) return false
        const g = JSON.parse(raw) as GameState
        if (!g?.players?.length) return false
        clearTimers()
        g.rolling = false
        if (g.phase === 'moving') g.phase = 'idle'
        set({ g, mode: 'local', net: IDLE_NET, inspecting: null })
        afterUpdate()
        return true
      } catch {
        return false
      }
    },

    quit: () => {
      clearTimers()
      if (get().mode === 'online') {
        get().leaveRoom()
        return
      }
      localStorage.removeItem(SAVE_KEY)
      set({ g: null, inspecting: null })
    },

    apply,

    /* ---------------- ห้องออนไลน์ ---------------- */

    createRoom: (name, characterId, professionId) => {
      clearTimers()
      set({ mode: 'online', g: null, inspecting: null, net: { ...IDLE_NET, status: 'connecting' } })
      ensureNet().connect({ t: 'create', name, characterId, professionId })
    },

    joinRoom: (code, name, characterId, professionId) => {
      clearTimers()
      set({ mode: 'online', g: null, inspecting: null, net: { ...IDLE_NET, status: 'connecting' } })
      ensureNet().connect({ t: 'join', code: code.toUpperCase(), name, characterId, professionId })
    },

    tryRejoin: () => {
      const saved = readToken()
      if (!saved) return false
      clearTimers()
      set({ mode: 'online', g: null, inspecting: null, net: { ...IDLE_NET, status: 'connecting' } })
      ensureNet().connect({ t: 'rejoin', code: saved.code, token: saved.token })
      return true
    },

    updateSeat: (patch) => net?.send({ t: 'seat', ...patch }),
    addBot: () => net?.send({ t: 'addBot' }),
    removeSeat: (id) => net?.send({ t: 'removeSeat', id }),
    updateSettings: (settings) => net?.send({ t: 'settings', settings }),
    startOnline: () => net?.send({ t: 'start' }),

    leaveRoom: () => {
      net?.close()
      net = null
      try {
        sessionStorage.removeItem(TOKEN_KEY)
      } catch {
        /* ไม่เป็นไร */
      }
      set({ mode: 'local', g: null, net: IDLE_NET, inspecting: null })
    },

    dismissNetError: () => set((s) => ({ net: { ...s.net, error: null } })),

    /* ---------------- คำสั่งในเกม ---------------- */

    roll: () => send({ t: 'roll' }),
    useReroll: () => send({ t: 'reroll' }),
    acceptRoll: () => send({ t: 'acceptRoll' }),
    confirm: () => send({ t: 'confirm' }),
    buy: (mode) => send({ t: 'buy', mode }),
    pass: () => send({ t: 'skipBuy' }),
    takeDeal: (cardId) => send({ t: 'takeDeal', cardId }),
    passDeal: () => send({ t: 'passDeal' }),
    // แตะพอร์ตเมื่อไหร่ = กำลังคิดอยู่ อย่าเพิ่งจบตาให้
    upgrade: (tileId) => {
      get().holdTurn()
      send({ t: 'upgrade', tileId })
    },
    mortgage: (tileId) => {
      get().holdTurn()
      send({ t: 'mortgage', tileId })
    },
    unmortgage: (tileId) => {
      get().holdTurn()
      send({ t: 'unmortgage', tileId })
    },
    dumpProperty: (tileId) => {
      get().holdTurn()
      send({ t: 'sellProperty', tileId })
    },
    dumpAsset: (uid) => {
      get().holdTurn()
      send({ t: 'sellAsset', uid })
    },
    bail: () => send({ t: 'bail' }),
    finishTurn: () => send({ t: 'endTurn' }),
    surrender: () => send({ t: 'surrender' }),

    inspect: (id) => set({ inspecting: id }),
    toggleRules: (v) => set((s) => ({ showRules: v ?? !s.showRules })),

    botStep: () =>
      apply((g) => {
        if (!cur(g).isBot) return
        botStep(g)
      }),
  }
})

export function hasSave(): boolean {
  try {
    return !!localStorage.getItem(SAVE_KEY)
  } catch {
    return false
  }
}

export function hasRoomToken(): boolean {
  return readToken() !== null
}
